/** external */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { EmptyState, ToggleSwitch } from "@toddle-edu/ds-web";
import _ from "lodash";
import { NoAssignmentIllustration } from "@toddle-edu/ds-theme";

/** internal */
import { useI18n } from "Hooks";
import {
  getSideBarOptions,
  getSideBarOptionsV2,
} from "Worksheet/modules/WorksheetUtils";
import {
  MODE_ASSESSMENT_VIEW,
  MODE_RESTRICTED_ASSESSMENT_VIEW,
} from "Worksheet/modules/WorksheetConstants";

/** relative */
import classes from "./ViewWorksheet.scss";
import SideBar from "../SideBar";
import SectionFeed from "../SectionFeed";
import StudentWorksheetInstructions from "../StudentWorksheetSubmission/StudentWorksheetInstructions";
import classNames from "classnames";

const ViewWorksheet = props => {
  const {
    mode,
    data,
    setWorksheetContainerRef,
    setScrollContainerRef,
    setQuestionRef,
    isFromLibrary,
    isToddleLibraryPortal,
    assignmentId,
    isQueriesV2Enabled,
    isFromToddlePortal,
    worksheetInstructions,
  } = props;

  const { t } = useI18n(["common"]);
  const [hideAnswers, setHideAnswers] = useState(false);

  //WORKSHEET_RESTRUCTURING: Use getSideBarOptionsV2 when queriesV2 is enabled
  const _getSideBarOptions = isQueriesV2Enabled
    ? getSideBarOptionsV2
    : getSideBarOptions;

  const { questionCount, totalMarks, options } = _getSideBarOptions({
    data,
    mode,
    t,
  });

  const contentClass = classNames({
    [classes.content]: !isFromLibrary,
    [classes.contentRightPane]: isFromLibrary,
  });

  const showAnswerSwitch =
    mode === MODE_ASSESSMENT_VIEW &&
    !isToddleLibraryPortal &&
    !isFromLibrary &&
    !_.isEmpty(options);

  const showInstructions = !_.isEmpty(worksheetInstructions);

  useEffect(() => {
    if (showAnswerSwitch) {
      setHideAnswers(true);
    }
  }, []);

  const showEmptyState = _.isEmpty(options) && mode === MODE_ASSESSMENT_VIEW;

  const changeMode = () => setHideAnswers(!hideAnswers);
  return (
    <div className={classes.container} ref={setWorksheetContainerRef}>
      {isFromLibrary ? null : (
        <SideBar
          mode={mode}
          questionCount={questionCount}
          totalMarks={totalMarks}
          options={options}
          isFromToddlePortal={isFromToddlePortal}
        />
      )}
      <div className={contentClass} ref={setScrollContainerRef}>
        {showAnswerSwitch ? (
          <div className={classes.hideAnswerContainer}>
            <div className="text-label-2">
              {t("worksheet:show_correct_answers_and_teacher_notes")}
            </div>
            <ToggleSwitch
              value={hideAnswers}
              onClick={changeMode}
              testId="worksheet-viewWorksheet-hideAnswer"
            />
          </div>
        ) : null}
        {showInstructions ? (
          <div className={classes.instructionsContainer}>
            <StudentWorksheetInstructions
              value={worksheetInstructions}
              t={t}
              openInstructions={true}
              isStudentEndUIRevampEnabled={false}
            />
          </div>
        ) : null}
        <SectionFeed
          sections={data}
          mode={hideAnswers ? MODE_RESTRICTED_ASSESSMENT_VIEW : mode}
          setQuestionRef={setQuestionRef}
          isFromLibrary={isFromLibrary}
          assignmentId={assignmentId}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
        {showEmptyState && (
          <EmptyState
            title={t("worksheet:no_questions_added")}
            subtitle={t("worksheet:no_questions_added_subtext")}
            illustration={NoAssignmentIllustration}
            dsVersion="2.0"
          />
        )}
      </div>
    </div>
  );
};

ViewWorksheet.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isFromLibrary: PropTypes.bool,
  assignmentId: PropTypes.string,
  isQueriesV2Enabled: PropTypes.bool,
};

ViewWorksheet.defaultProps = {
  data: {},
  mode: MODE_ASSESSMENT_VIEW,
  isQueriesV2Enabled: false,
};

export default ViewWorksheet;
